import { useEffect, useState } from "react";
import VerifyCard from "../VerifyCard";
import DocumentModal from "../DocumentModal";
import PhysicalVerificationModal from "../PhysicalVerificationModal";
import { useVerificationDetails } from "hooks/api/useAccountVerify";
import SuccessModal from "components/Account/components/successModal/index ";
import { useSearchParams } from "react-router-dom";

const STATUS_MAP = {
  1: "REQUESTED",
  2: "APPROVED",
  3: "REJECTED",
  4: "PAYMENT INITIATED",
  5: "PAYMENT FAILED",
  deleted: "DELETED",
};

function CardList() {
  const [searchParams] = useSearchParams();
  const [isDocumentModalOpen, setDocumentModalOpen] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [isPhysicalModalOpen, setPhysicalModalOpen] = useState(false);
  const txnid = searchParams.get("txnid");

  useEffect(() => {
    if (txnid) {
      setPhysicalModalOpen(true);
    }
  }, [txnid]);

  const { data: verificationDetails, isLoading: isVerificationLoading } =
    useVerificationDetails();

  const verifications = verificationDetails?.verification_details || [];
  const canSendIdVerification = verificationDetails?.can_send_id_verification;
  const canSendPhysicalVerification =
    verificationDetails?.can_send_manual_verification;
  const physicalVerificationAmount =
    verificationDetails?.physical_verification_amount;

  /**
   * Gets the latest verification entry for a specific type
   * This function handles cases where multiple entries exist for the same verification type
   * by returning the entry with the highest ID (most recent)
   */
  const getVerification = (type) => {
    const verificationsOfType = verifications.filter(
      (v) => v.verification_type === type,
    );
    return verificationsOfType.length > 0
      ? verificationsOfType.reduce((latest, current) =>
          current.id > latest.id ? current : latest,
        )
      : {};
  };

  // Get latest verification entries for each type
  const idVerification = getVerification(1);
  const physicalVerification = getVerification(2);

  /**
   * Determine ID verification status
   * Priority: deleted status > regular status mapping
   */
  const idStatus = idVerification.is_deleted
    ? STATUS_MAP["deleted"]
    : STATUS_MAP[idVerification.status] || "";

  /**
   * Determine physical verification status
   * Priority: deleted status > regular status mapping
   */
  const physicalStatus = physicalVerification.is_deleted
    ? STATUS_MAP["deleted"]
    : STATUS_MAP[physicalVerification.status] || "";

  // Extract unique verification code for physical verification
  const physicalCode = physicalVerification.unique_verification_code;

  const shouldShowIdBackArrow =
    verifications.length === 0 ||
    (verifications.length === 1 &&
      physicalVerification.verification_type === 2) ||
    (verifications.length === 1 &&
      idVerification.verification_type === 1 &&
      idStatus === "REJECTED") ||
    (verifications.length === 2 && idStatus === "REJECTED");

  const shouldShowPhysicalBackArrow =
    verifications.length === 0 ||
    !physicalVerification.verification_type ||
    (verifications.length === 1 && idVerification.verification_type === 1) ||
    (verifications.length === 1 &&
      physicalVerification.verification_type === 2 &&
      physicalStatus === "REJECTED") ||
    (verifications.length === 2 && physicalStatus === "REJECTED") ||
    physicalStatus === 5;

  console.log("idVerification", verificationDetails);

  return (
    <>
      <div className="verify-cards">
        <VerifyCard
          title="Upload a valid ID"
          description="Please upload a government verified document/ID"
          status={idStatus}
          variant="id"
          onClick={() =>
            canSendIdVerification
              ? setDocumentModalOpen(true)
              : setDocumentModalOpen(false)
          }
          showBackArrow={shouldShowIdBackArrow}
        />

        <VerifyCard
          title="Physical verification"
          description="Select your Pin code and verify your profile with ease!"
          status={physicalStatus}
          variant="physical"
          uniqueCode={physicalCode}
          onClick={() =>
            canSendPhysicalVerification
              ? setPhysicalModalOpen(true)
              : setPhysicalModalOpen(false)
          }
          showBackArrow={shouldShowPhysicalBackArrow}
        />
      </div>

      <DocumentModal
        open={isDocumentModalOpen}
        setOpen={setDocumentModalOpen}
        onOk={() => setSuccessModalOpen(true)}
      />

      <SuccessModal
        open={isSuccessModalOpen}
        onCancel={() => setSuccessModalOpen(false)}
        onOk={() => {
          setSuccessModalOpen(false);
        }}
        title="Your document has been submitted successfully"
      />

      <PhysicalVerificationModal
        open={isPhysicalModalOpen}
        setOpen={setPhysicalModalOpen}
        amount={physicalVerificationAmount}
      />
    </>
  );
}

export default CardList;
